import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData } from '../../../services/admin/blogs/blogsServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

const ApprovedBlogs = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [authorFilter, setAuthorFilter] = useState('');
    const [subjectFilter, setSubjectFilter] = useState('');
    const [subDomainFilter, setSubDomainFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const data = await getData({ approval: 1 });
            setDataList(data || []);
        } catch (error) {
            console.error('Failed to fetch blogs:', error);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                await fetchData();
                await new Promise(resolve => setTimeout(resolve, 1000));
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [fetchData]);

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const filteredData = useMemo(() => {
        const startDateObj = startDate ? new Date(startDate) : null;
        const endDateObj = endDate ? new Date(endDate + 'T23:59:59') : null;

        return dataList
            .filter((blog) => {
                const matchesSearch =
                    (blog.title || '')
                        .toLowerCase()
                        .includes(searchText.toLowerCase()) ||
                    (blog.author || '')
                        .toLowerCase()
                        .includes(searchText.toLowerCase()) ||
                    (blog.subject || '')
                        .toLowerCase()
                        .includes(searchText.toLowerCase());

                const matchAuthor =
                    !authorFilter || blog.author === authorFilter;

                const matchSubject =
                    !subjectFilter || blog.subject === subjectFilter;

                const matchSubDomain =
                    !subDomainFilter ||
                    blog.sub_domain === subDomainFilter;

                let matchDate = true;

                if (startDateObj && endDateObj) {
                    const blogDate = new Date(blog.updatedAt);
                    matchDate =
                        blogDate >= startDateObj &&
                        blogDate <= endDateObj;
                }

                return (
                    matchesSearch &&
                    matchAuthor &&
                    matchSubject &&
                    matchSubDomain &&
                    matchDate
                );
            })
            .sort(
                (a, b) =>
                    new Date(b.updatedAt) -
                    new Date(a.updatedAt)
            );
    }, [
        dataList,
        searchText,
        authorFilter,
        subjectFilter,
        subDomainFilter,
        startDate,
        endDate,
    ]);

    const columns = [
        {
            name: '#',
            selector: (row, index) =>
                (currentPage - 1) * 10 + (index + 1),
            width: '70px',
        },
        {
            name: 'Image',
            cell: (row) =>
                row.blog_img ? (
                    <img
                        src={row.blog_img}
                        alt={row.alt_name || 'Blog'}
                        style={{
                            width: '80px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                        }}
                    />
                ) : (
                    <span>No Image</span>
                ),
            width: '110px',
        },
        {
            name: 'Title',
            selector: (row) => row.title,
            sortable: true,
            grow: 2,
            wrap: true,
        },
        {
            name: 'Author',
            selector: (row) => row.author || '-',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Subject',
            selector: (row) => row.subject || '-',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Sub Domain',
            selector: (row) => row.sub_domain || '-',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Status',
            cell: (row) => (
                <span
                    className={`badge ${
                        Number(row.approval) === 1
                            ? 'bg-success'
                            : 'bg-danger'
                    }`}
                >
                    {Number(row.approval) === 1
                        ? 'Approved'
                        : 'Pending'}
                </span>
            ),
            width: '120px',
        },
    ];

    const ExpandableRow = ({ data }) => {
        let remarksHistory = data.remarks_history || [];
        if (typeof remarksHistory === 'string') {
            try { remarksHistory = JSON.parse(remarksHistory); } catch { remarksHistory = []; }
        }

        return (
            <div className="p-3 bg-light border">
                <table className="table table-bordered">
                    <tbody>
                        <tr>
                            <td width="20%">
                                <strong>Title</strong>
                            </td>
                            <td>{data.title}</td>
                        </tr>

                        <tr>
                            <td>
                                <strong>Author</strong>
                            </td>
                            <td>{data.author}</td>
                        </tr>

                        <tr>
                            <td>
                                <strong>Subject</strong>
                            </td>
                            <td>{data.subject}</td>
                        </tr>

                        <tr>
                            <td>
                                <strong>Alt Name</strong>
                            </td>
                            <td>{data.alt_name}</td>
                        </tr>
                        <tr>
                            <td>
                                <strong>Remarks History</strong>
                            </td>
                            <td>
                                {Array.isArray(remarksHistory) && remarksHistory.length > 0 ? (
                                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                        {[...remarksHistory].reverse().map((item, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    padding: '6px 10px',
                                                    marginBottom: 6,
                                                    borderLeft: '3px solid #0d6efd',
                                                    background: '#fff',
                                                    borderRadius: '0 4px 4px 0',
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-baseline">
                                                    <small className="text-muted d-block mb-1">
                                                        {new Date(item.timestamp).toLocaleString('en-IN', {
                                                            day: '2-digit', month: 'short', year: 'numeric',
                                                            hour: '2-digit', minute: '2-digit',
                                                        })}
                                                    </small>
                                                    {item.user && (
                                                        <span
                                                            className={`badge ${
                                                                item.user === 'admin' ? 'bg-secondary' : 'bg-info text-dark'
                                                            }`}
                                                            style={{ fontSize: 10 }}
                                                        >
                                                            {item.user}
                                                        </span>
                                                    )}
                                                </div>
                                                <span style={{ fontSize: 14 }}>{item.remark}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-muted small">No remarks added</span>
                                )}
                            </td>
                        </tr>

                        <tr>
                            <td>
                                <strong>Last Updated</strong>
                            </td>
                            <td>
                                {new Date(
                                    data.updatedAt
                                ).toLocaleString()}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="d-flex">
            <Sidebar />

            <div className="main-content w-100">
                <Header />

                <div className="row m-3 d-flex flex-column gap-3">
                    <div className="d-flex gap-2 flex-wrap">

                        <div className="col-md-2">
                            <span className="fs-5 text-dark">
                                Author
                            </span>

                            <select
                                className="form-control"
                                value={authorFilter}
                                onChange={(e) =>
                                    setAuthorFilter(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    All Authors
                                </option>

                                {Array.from(
                                    new Set(
                                        dataList
                                            .map(
                                                (item) =>
                                                    item.author
                                            )
                                            .filter(Boolean)
                                    )
                                )
                                    .sort()
                                    .map((author) => (
                                        <option
                                            key={author}
                                            value={author}
                                        >
                                            {author}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <span className="fs-5 text-dark">
                                Subject
                            </span>

                            <select
                                className="form-control"
                                value={subjectFilter}
                                onChange={(e) =>
                                    setSubjectFilter(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    All Subjects
                                </option>

                                {Array.from(
                                    new Set(
                                        dataList
                                            .map(
                                                (item) =>
                                                    item.subject
                                            )
                                            .filter(Boolean)
                                    )
                                )
                                    .sort()
                                    .map((subject) => (
                                        <option
                                            key={subject}
                                            value={subject}
                                        >
                                            {subject}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <span className="fs-5 text-dark">
                                Sub Domain
                            </span>

                            <select
                                className="form-control"
                                value={subDomainFilter}
                                onChange={(e) =>
                                    setSubDomainFilter(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    All Sub Domains
                                </option>

                                {Array.from(
                                    new Set(
                                        dataList
                                            .map(
                                                (item) =>
                                                    item.sub_domain
                                            )
                                            .filter(Boolean)
                                    )
                                )
                                    .sort()
                                    .map((subDomain) => (
                                        <option
                                            key={subDomain}
                                            value={subDomain}
                                        >
                                            {subDomain}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <span className="fs-5 text-dark">
                                Start Date
                            </span>

                            <input
                                type="date"
                                className="form-control"
                                value={startDate}
                                onChange={(e) =>
                                    setStartDate(
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div className="col-md-2">
                            <span className="fs-5 text-dark">
                                End Date
                            </span>

                            <input
                                type="date"
                                className="form-control"
                                value={endDate}
                                onChange={(e) =>
                                    setEndDate(
                                        e.target.value
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div
                        className="d-flex justify-content-center align-items-center w-100"
                        style={{ minHeight: '300px' }}
                    >
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <div className="container-fluid p-4">

                            <div className="row mb-3">
                                <div className="col-12">
                                    <p className="fs-4 text-dark">
                                        Blogs / Approved Blogs
                                    </p>
                                </div>
                            </div>

                            <DataTable
                                columns={columns}
                                data={filteredData}
                                pagination
                                highlightOnHover
                                responsive
                                striped
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[
                                    10,
                                    20,
                                    50,
                                    100,
                                    200,
                                    500,
                                ]}
                                onChangePage={
                                    handlePageChange
                                }
                                subHeader
                                subHeaderComponent={
                                    <input
                                        className="form-control"
                                        style={{
                                            maxWidth: '250px',
                                        }}
                                        type="text"
                                        placeholder="Search..."
                                        value={searchText}
                                        onChange={
                                            handleSearchChange
                                        }
                                    />
                                }
                                expandableRows
                                expandableRowsComponent={
                                    ExpandableRow
                                }
                            />
                        </div>

                        <Footer />
                    </>
                )}
            </div>
        </div>
    );
};

export default ApprovedBlogs;
